/* eslint-disable @typescript-eslint/no-explicit-any */
import { ErrorHandler, Inject, Injectable, NgZone } from '@angular/core';
import { DOCUMENT } from '@angular/common';

import { ErrorChunkDialogComponent } from '../../components/error-chunk-dialog.ts/error-chunk-dialog.component';
import * as Sentry from '@sentry/angular';
import { TimeoutError } from 'rxjs';
import { HttpErrorResponse } from '@angular/common/http';
import { AteModalService } from './ate-modal/ate-modal.service';

@Injectable()
export class GlobalErrorHandler implements ErrorHandler {
  constructor(
    @Inject(DOCUMENT) readonly document: Document,
    private _ateModalService: AteModalService,
    private _ngZone: NgZone
  ) {}

  get window(): Window {
    return this.document.defaultView;
  }

  handleError(error: any): void {
    // Matches every browser's "lazy route failed to load" message. The
    // `application` (esbuild) builder uses native ESM dynamic import() for
    // lazy routes, so the old webpack-only `Loading chunk N failed` no longer
    // fires — Chrome/Edge, Firefox and Safari each throw a different string.
    // Also covers the MIME mismatch a stale deploy produces when a 404'd chunk
    // is served the index.html fallback as text/html.
    const chunkFailedMessage =
      /Loading chunk [\w-]+ failed|Failed to fetch dynamically imported module|error loading dynamically imported module|Importing a module script failed|Failed to load module script/i;

    // The message can sit on the error itself, on its cause (when the router/zone
    // re-wraps the original), or the error may be a raw string. Read those fields
    // directly — never stringify the whole error, so a custom toString() can't
    // throw from inside the global handler.
    const message =
      typeof error === 'string' ? error : `${error?.message ?? ''} ${error?.cause?.message ?? ''}`;

    if (chunkFailedMessage.test(message)) {
      this.handleChunkFailedError();
    } else {
      // Sentry log
      try {
        if (!(error instanceof HttpErrorResponse) && !(error instanceof TimeoutError)) {
          Sentry.captureException(error);
          // Log all non-chunk errors to the console
          console.error(error);
        }
      } catch (e) {
        /* empty */
      }
    }
  }

  public handleChunkFailedError() {
    this._ngZone.run(() => {
      const dialogRef = this._ateModalService.open<ErrorChunkDialogComponent>(ErrorChunkDialogComponent, {
        hasCta: true,
        ctaText: 'Refresh',
        width: '480px'
      });

      dialogRef.afterClosed().subscribe(result => {
        if (result) {
          this.window.location.reload();
        }
      });
    });
  }
}
